// DefaultLocationModal.jsx
// A self-contained modal button component for setting default location coordinates
// from a map URL, showing only visibility-allowed employees.
// Usage: <DefaultLocationModal theme={theme} language={language} isRTL={isRTL} />

import React, { useState, useEffect, useCallback } from "react";
import { MapPin, X, Search, CheckCircle, AlertCircle, Loader, Link, Plus, Trash2 } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import { useProfile } from "../../../../hooks/useProfile";
// أضف هذا الاستيراد في أعلى الملف
import ReactDOM from "react-dom";
const BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";

// ─── translations ────────────────────────────────────────────────────────────
const t = {
  en: {
    btnLabel: "Default Location",
    modalTitle: "Set Default Location",
    modalSubtitle: "Set a default map coordinate for an employee using a Google Maps link.",
    searchPlaceholder: "Search by name or ID…",
    noEmployees: "No employees found.",
    urlLabel: "Google Maps URL",
    urlPlaceholder: "Paste Google Maps link here…",
    radiusLabel: "Radius (m)",
    namePlaceholder: "Location name (optional, e.g. HQ, Branch)",
    addLocation: "Add another location",
    locationN: "Location",
    removeLocation: "Remove location",
    loadingLocations: "Loading saved locations…",
    noLocations: "No default locations saved yet. Add one below.",
    save: "Save Location",
    saving: "Saving…",
    cancel: "Cancel",
    successMsg: "Default locations saved successfully!",
    clearedMsg: "All default locations cleared.",
    errorMsg: "Failed to save coordinates.",
    urlRequired: "Please paste at least one map URL.",
    empRequired: "Please select an employee first.",
    selected: "Selected",
    coords: "Coordinates",
  },
  ar: {
    btnLabel: "الموقع الافتراضي",
    modalTitle: "تعيين الموقع الافتراضي",
    modalSubtitle: "قم بتعيين إحداثيات خريطة افتراضية للموظف باستخدام رابط Google Maps.",
    searchPlaceholder: "ابحث بالاسم أو الرقم…",
    noEmployees: "لا يوجد موظفون.",
    urlLabel: "رابط Google Maps",
    urlPlaceholder: "الصق رابط Google Maps هنا…",
    radiusLabel: "النطاق (متر)",
    namePlaceholder: "اسم الموقع (اختياري، مثل: المقر، الفرع)",
    addLocation: "إضافة موقع آخر",
    locationN: "الموقع",
    removeLocation: "حذف الموقع",
    loadingLocations: "جارٍ تحميل المواقع المحفوظة…",
    noLocations: "لا توجد مواقع افتراضية محفوظة بعد. أضف واحدًا أدناه.",
    save: "حفظ الموقع",
    saving: "جارٍ الحفظ…",
    cancel: "إلغاء",
    successMsg: "تم حفظ المواقع الافتراضية بنجاح!",
    clearedMsg: "تم مسح جميع المواقع الافتراضية.",
    errorMsg: "فشل حفظ الإحداثيات.",
    urlRequired: "الرجاء لصق رابط خريطة واحد على الأقل.",
    empRequired: "الرجاء اختيار موظف أولاً.",
    selected: "المحدد",
    coords: "الإحداثيات",
  },
};

// ─── helpers ─────────────────────────────────────────────────────────────────
const resolveManagerContext = () => {
  try {
    const token = localStorage.getItem("token");
    let payload = null;
    if (token) {
      const parts = token.split(".");
      if (parts.length === 3) {
        let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (b64.length % 4) b64 += "=";
        payload = JSON.parse(atob(b64));
      }
    }
    return {
      clientName:
        localStorage.getItem("clientName") ||
        payload?.clientName ||
        payload?.employeeClientName ||
        "",
      managerCode:
        localStorage.getItem("infinityCode") ||
        payload?.infinityCode ||
        payload?.employeeInfinityCode ||
        null,
    };
  } catch {
    return { clientName: "", managerCode: null };
  }
};

// ─── main component ───────────────────────────────────────────────────────────
const DefaultLocationModal = ({ theme = "light", language = "en", isRTL = false }) => {
  const tr = t[language] || t.en;
  const isDark = theme === "dark";
  const { profile } = useProfile();

  // modal visibility
  const [open, setOpen] = useState(false);

  // employee list
  const [employees, setEmployees] = useState([]);
  const [loadingEmps, setLoadingEmps] = useState(false);
  const [search, setSearch] = useState("");

  // selection
  const [selectedEmp, setSelectedEmp] = useState(null);

  // default locations. An employee may have more than one.
  // Existing entries: { isExisting: true, name, latitude, longitude, radius }.
  // New entries:      { isExisting: false, name, mapUrl, radius }.
  const newEntry = () => ({ isExisting: false, name: "", mapUrl: "", radius: "500" });
  const [locations, setLocations] = useState([newEntry()]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const updateLocation = (idx, patch) => {
    setLocations((prev) => prev.map((loc, i) => (i === idx ? { ...loc, ...patch } : loc)));
    setFeedback(null);
  };
  const addLocation = () => {
    setLocations((prev) => [...prev, newEntry()]);
    setFeedback(null);
  };
  const removeLocation = (idx) => {
    setLocations((prev) => prev.filter((_, i) => i !== idx));
    setFeedback(null);
  };

  // save state
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', msg: string }
  const [allowDefaultLocation, setAllowDefaultLocation] = useState(true);

  // ── load employees when modal opens ────────────────────────────────────────
  const loadEmployees = useCallback(async () => {
    setLoadingEmps(true);
    setEmployees([]);
    try {
      const { clientName: fallbackClient, managerCode: fallbackCode } = resolveManagerContext();
      const clientName = profile?.clientName || fallbackClient;
      const managerCode = profile?.infinityCode || fallbackCode;
      const viewType = localStorage.getItem("managerViewType") || "manager";

      const usp = new URLSearchParams();
      if (clientName) usp.append("clientName", clientName);
      usp.append("viewType", viewType);
      if (viewType === "manager" && managerCode)
        usp.append("managerInfinityCode", managerCode);
      usp.append("forDefaultLocation", "true");

      const resp = await apiFetch(
        `${BASE_URL}/employees/visibility?${usp.toString()}`,
        { method: "GET", headers: { "Accept-Language": language } }
      );
      if (!resp.ok) throw new Error("fetch failed");
      const json = await resp.json();
      const list = json.employees || json.data || [];
      setEmployees(
        list.map((e) => ({
          infinityCode: e.infinityCode || e.id,
          name:
            language === "ar"
              ? e.arabicName || e.sr || e.name || String(e.infinityCode)
              : e.sr || e.name || e.arabicName || String(e.infinityCode),
          clientName: e.clientName,
        }))
      );
    } catch {
      setEmployees([]);
    } finally {
      setLoadingEmps(false);
    }
  }, [language]);

  useEffect(() => {
    if (open) {
      loadEmployees();
      setSelectedEmp(null);
      setLocations([newEntry()]);
      setFeedback(null);
      setSearch("");
      setAllowDefaultLocation(true);
    }
  }, [open, loadEmployees]);

  // ── load the selected employee's existing default locations ─────────────────
  useEffect(() => {
    if (!selectedEmp) return;
    let mounted = true;
    (async () => {
      setLoadingLocations(true);
      setFeedback(null);
      try {
        const resp = await apiFetch(
          `${BASE_URL}/employees/location/${selectedEmp.infinityCode}/default-coordinates`,
          { method: "GET", headers: { "Accept-Language": language } }
        );
        const data = await resp.json();
        if (!mounted) return;
        const existing = (resp.ok && data.success ? data.data?.defaultLocations : null) || [];
        const isAllowed = data.data?.allowDefaultLocation ?? true;
        setAllowDefaultLocation(isAllowed);
        if (!isAllowed) {
          setFeedback({
            type: "error",
            msg: language === "ar"
              ? "تنبيه: ميزة الموقع الافتراضي معطلة لهذا الموظف في إعدادات الشركة."
              : "Warning: Default Location feature is disabled for this employee in Company Configuration.",
          });
        }
        setLocations(
          existing.length
            ? existing.map((loc) => ({
                isExisting: true,
                name: loc.name ?? "",
                latitude: loc.latitude,
                longitude: loc.longitude,
                radius: String(loc.radius ?? 500),
              }))
            : [newEntry()]
        );
      } catch {
        if (mounted) setLocations([newEntry()]);
      } finally {
        if (mounted) setLoadingLocations(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [selectedEmp, language]);

  // ── filtered list ──────────────────────────────────────────────────────────
  const filtered = employees.filter((e) => {
    const q = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      String(e.infinityCode).toLowerCase().includes(q)
    );
  });

  // ── save handler ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setFeedback(null);
    if (!allowDefaultLocation) {
      setFeedback({
        type: "error",
        msg: language === "ar"
          ? "تنبيه: ميزة الموقع الافتراضي معطلة لهذا الموظف في إعدادات الشركة."
          : "Default Location feature is disabled for this employee in Company Configuration.",
      });
      return;
    }
    if (!selectedEmp) {
      setFeedback({ type: "error", msg: tr.empRequired });
      return;
    }
    const payloadLocations = locations
      .filter((loc) => loc.isExisting || loc.mapUrl.trim())
      .map((loc) => {
        const radius = Number(loc.radius) > 0 ? Number(loc.radius) : 500;
        const name = loc.name?.trim() || null;
        return loc.isExisting
          ? { name, latitude: loc.latitude, longitude: loc.longitude, radius }
          : { name, mapUrl: loc.mapUrl.trim(), radius };
      });

    // An empty list is allowed: it clears all default locations.
    setSaving(true);
    try {
      const resp = await apiFetch(
        `${BASE_URL}/employees/location/${selectedEmp.infinityCode}/default-coordinates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": language,
          },
          body: JSON.stringify({ locations: payloadLocations }),
        }
      );
      const data = await resp.json();
      if (!resp.ok || !data.success) throw new Error(data.error || "error");

      const count = data.data?.defaultLocations?.length ?? payloadLocations.length;
      setFeedback({
        type: "success",
        msg: count === 0 ? tr.clearedMsg : `${tr.successMsg} (${count})`,
      });
      // auto-close after 2.5 s
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      setFeedback({ type: "error", msg: err.message || tr.errorMsg });
    } finally {
      setSaving(false);
    }
  };

  // ── style tokens ───────────────────────────────────────────────────────────
  const surface = isDark ? "#1e293b" : "#ffffff";
  const surfaceAlt = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)";
  const border = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
  const textPrimary = isDark ? "#f1f5f9" : "#0f172a";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const inputBg = isDark ? "rgba(71,85,105,0.4)" : "rgba(226,232,240,0.6)";
  const inputBorder = isDark
    ? "1px solid rgba(148,163,184,0.3)"
    : "1px solid rgba(148,163,184,0.3)";
  const accent = "#2e7cba";
  const accentHover = "#2563a8";

  const btnBase = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.15)",
    fontWeight: 500,
    fontSize: 13,
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexDirection: isRTL ? "row-reverse" : "row",
  };

  return (
    <>
      {/* ── trigger button (matches sibling action buttons in Attendance.jsx) ── */}
      <button
        onClick={() => setOpen(true)}
        style={{
          ...btnBase,
          background: isDark ? "rgba(30,41,59,0.8)" : "rgba(51,65,85,0.12)",
          color: isDark ? "#f1f5f9" : "#1e293b",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = isDark
            ? "rgba(30,41,59,1)"
            : "rgba(51,65,85,0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = isDark
            ? "rgba(30,41,59,0.8)"
            : "rgba(51,65,85,0.12)";
        }}
      >
        <MapPin size={16} />
        {tr.btnLabel}
      </button>

      {/* ── modal ─────────────────────────────────────────────────────────────── */}
      {open && ReactDOM.createPortal(
  <div
    onClick={() => !saving && setOpen(false)}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 999999,   // ← رقم أعلى من أي عنصر آخر
      direction: isRTL ? "rtl" : "ltr",
    }}
  >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: surface,
              borderRadius: 20,
              width: "min(96vw, 860px)",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.7)"
                : "0 24px 64px rgba(0,0,0,0.18)",
              overflow: "hidden",
            }}
          >
            {/* header */}
            <div
              style={{
                padding: "24px 28px",
                borderBottom: `1px solid ${border}`,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 16,
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${accent}, #2e7cba)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MapPin size={22} color="#fff" />
                </div>
                <div>
                  <h2
                    style={{
                      margin: 0,
                      fontSize: 20,
                      fontWeight: 700,
                      color: textPrimary,
                    }}
                  >
                    {tr.modalTitle}
                  </h2>
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: 13,
                      color: textSecondary,
                    }}
                  >
                    {tr.modalSubtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={() => !saving && setOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: saving ? "not-allowed" : "pointer",
                  color: textSecondary,
                  padding: 4,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <X size={22} />
              </button>
            </div>

            {/* body: two-column layout */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                flex: 1,
                overflow: "hidden",
              }}
            >
              {/* LEFT — employee list */}
              <div
                style={{
                  borderRight: isRTL ? "none" : `1px solid ${border}`,
                  borderLeft: isRTL ? `1px solid ${border}` : "none",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                {/* search */}
                <div
                  style={{ padding: "16px 20px 10px", flexShrink: 0 }}
                >
                  <div style={{ position: "relative" }}>
                    <Search
                      size={15}
                      style={{
                        position: "absolute",
                        top: "50%",
                        [isRTL ? "right" : "left"]: 12,
                        transform: "translateY(-50%)",
                        color: textSecondary,
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={tr.searchPlaceholder}
                      style={{
                        width: "100%",
                        padding: isRTL
                          ? "10px 36px 10px 12px"
                          : "10px 12px 10px 36px",
                        borderRadius: 10,
                        border: inputBorder,
                        background: inputBg,
                        color: textPrimary,
                        fontSize: 13,
                        outline: "none",
                        direction: isRTL ? "rtl" : "ltr",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>

                {/* list */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "4px 12px 16px",
                    scrollbarWidth: "thin",
                    scrollbarColor: isDark
                      ? "rgba(148,163,184,0.6) rgba(255,255,255,0.03)"
                      : "rgba(100,116,139,0.5) rgba(0,0,0,0.02)",
                  }}
                >
                  {loadingEmps ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: 160,
                        gap: 12,
                        color: textSecondary,
                      }}
                    >
                      <Loader
                        size={28}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                      <span style={{ fontSize: 13 }}>Loading…</span>
                    </div>
                  ) : filtered.length === 0 ? (
                    <div
                      style={{
                        padding: "40px 16px",
                        textAlign: "center",
                        color: textSecondary,
                        fontSize: 13,
                      }}
                    >
                      {tr.noEmployees}
                    </div>
                  ) : (
                    filtered.map((emp) => {
                      const isActive =
                        selectedEmp?.infinityCode === emp.infinityCode;
                      return (
                        <div
                          key={emp.infinityCode}
                          onClick={() => {
                            setSelectedEmp(emp);
                            setFeedback(null);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 12px",
                            borderRadius: 10,
                            cursor: "pointer",
                            marginBottom: 4,
                            background: isActive
                              ? isDark
                                ? "rgba(46,124,186,0.25)"
                                : "rgba(46,124,186,0.10)"
                              : "transparent",
                            border: isActive
                              ? `1px solid ${accent}55`
                              : "1px solid transparent",
                            transition: "all 0.15s ease",
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive)
                              e.currentTarget.style.background = surfaceAlt;
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive)
                              e.currentTarget.style.background = "transparent";
                          }}
                        >
                          {/* avatar */}
                          <div
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: isActive
                                ? `linear-gradient(135deg, ${accent}, #2e7cba)`
                                : isDark
                                ? "rgba(255,255,255,0.10)"
                                : "rgba(0,0,0,0.07)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 13,
                              fontWeight: 700,
                              color: isActive
                                ? "#fff"
                                : textSecondary,
                              flexShrink: 0,
                            }}
                          >
                            {emp.name.charAt(0).toUpperCase()}
                          </div>

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: isActive ? accent : textPrimary,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {emp.name}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: textSecondary,
                                marginTop: 1,
                              }}
                            >
                              #{emp.infinityCode}
                            </div>
                          </div>

                          {isActive && (
                            <CheckCircle
                              size={16}
                              color={accent}
                              style={{ flexShrink: 0 }}
                            />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* RIGHT — URL input + save */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "24px 28px",
                  gap: 20,
                  overflowY: "auto",
                  scrollbarWidth: "thin",
                  scrollbarColor: isDark
                    ? "rgba(148,163,184,0.6) rgba(255,255,255,0.03)"
                    : "rgba(100,116,139,0.5) rgba(0,0,0,0.02)",
                }}
              >
                {/* selected employee badge */}
                <div
                  style={{
                    padding: "12px 16px",
                    borderRadius: 12,
                    background: selectedEmp
                      ? isDark
                        ? "rgba(46,124,186,0.15)"
                        : "rgba(46,124,186,0.07)"
                      : surfaceAlt,
                    border: `1px solid ${
                      selectedEmp ? `${accent}44` : border
                    }`,
                    minHeight: 58,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    transition: "all 0.2s ease",
                  }}
                >
                  {selectedEmp ? (
                    <>
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, ${accent}, #2e7cba)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 15,
                          fontWeight: 700,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {selectedEmp.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: textPrimary,
                          }}
                        >
                          {selectedEmp.name}
                        </div>
                        <div
                          style={{ fontSize: 12, color: textSecondary }}
                        >
                          #{selectedEmp.infinityCode}
                        </div>
                      </div>
                    </>
                  ) : (
                    <span
                      style={{
                        fontSize: 13,
                        color: textSecondary,
                        fontStyle: "italic",
                      }}
                    >
                      ← {tr.searchPlaceholder.replace("…", "")}
                    </span>
                  )}
                </div>

                {/* default location entries */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {loadingLocations ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        padding: 20,
                        color: textSecondary,
                        fontSize: 13,
                      }}
                    >
                      <Loader size={16} style={{ animation: "spin 1s linear infinite" }} />
                      {tr.loadingLocations}
                    </div>
                  ) : (
                    <>
                      {locations.length === 0 && (
                        <p style={{ fontSize: 13, color: textSecondary, margin: 0, fontStyle: "italic" }}>
                          {tr.noLocations}
                        </p>
                      )}
                      {locations.map((loc, idx) => (
                        <div
                          key={idx}
                          style={{
                            border: inputBorder,
                            borderRadius: 12,
                            padding: 12,
                            background: surfaceAlt,
                            display: "flex",
                            flexDirection: "column",
                            gap: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 13,
                                fontWeight: 600,
                                color: textPrimary,
                              }}
                            >
                              {loc.isExisting ? <MapPin size={14} /> : <Link size={14} />}
                              {(loc.isExisting ? tr.coords : tr.urlLabel)} — {tr.locationN} {idx + 1}
                            </label>
                            <button
                              type="button"
                              onClick={() => removeLocation(idx)}
                              title={tr.removeLocation}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "none",
                                background: "transparent",
                                color: "#ef4444",
                                cursor: "pointer",
                                padding: 4,
                                borderRadius: 6,
                              }}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>

                          <input
                            type="text"
                            value={loc.name}
                            onChange={(e) => updateLocation(idx, { name: e.target.value })}
                            placeholder={tr.namePlaceholder}
                            style={{
                              width: "100%",
                              padding: "10px 14px",
                              borderRadius: 12,
                              border: inputBorder,
                              background: inputBg,
                              color: textPrimary,
                              fontSize: 13,
                              outline: "none",
                              boxSizing: "border-box",
                            }}
                          />

                          {loc.isExisting ? (
                            <div
                              style={{
                                padding: "10px 14px",
                                borderRadius: 12,
                                border: inputBorder,
                                background: inputBg,
                                color: textPrimary,
                                fontSize: 13,
                                fontFamily: "monospace",
                                direction: "ltr",
                              }}
                            >
                              {Number(loc.latitude).toFixed(6)}, {Number(loc.longitude).toFixed(6)}
                            </div>
                          ) : (
                            <textarea
                              value={loc.mapUrl}
                              onChange={(e) => updateLocation(idx, { mapUrl: e.target.value })}
                              placeholder={tr.urlPlaceholder}
                              rows={3}
                              style={{
                                width: "100%",
                                padding: "12px 14px",
                                borderRadius: 12,
                                border: inputBorder,
                                background: inputBg,
                                color: textPrimary,
                                fontSize: 13,
                                resize: "vertical",
                                outline: "none",
                                direction: "ltr",
                                fontFamily: "monospace",
                                boxSizing: "border-box",
                                transition: "border-color 0.2s",
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = accent;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = "";
                              }}
                            />
                          )}

                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <label
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: textPrimary,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {tr.radiusLabel}
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={loc.radius}
                              onChange={(e) => updateLocation(idx, { radius: e.target.value })}
                              style={{
                                width: 120,
                                padding: "8px 12px",
                                borderRadius: 10,
                                border: inputBorder,
                                background: inputBg,
                                color: textPrimary,
                                fontSize: 13,
                                outline: "none",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addLocation}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 6,
                          padding: "10px 14px",
                          borderRadius: 12,
                          border: `1px dashed ${accent}`,
                          background: "transparent",
                          color: accent,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        <Plus size={16} />
                        {tr.addLocation}
                      </button>

                      <p
                        style={{
                          fontSize: 11,
                          color: textSecondary,
                          margin: "2px 0 0",
                        }}
                      >
                        Supported: Google Maps links, short links (goo.gl/maps),
                        share URLs with @lat,lng, etc.
                      </p>
                    </>
                  )}
                </div>

                {/* feedback */}
                {feedback && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "12px 14px",
                      borderRadius: 10,
                      background:
                        feedback.type === "success"
                          ? isDark
                            ? "rgba(16,185,129,0.15)"
                            : "rgba(16,185,129,0.08)"
                          : isDark
                          ? "rgba(239,68,68,0.15)"
                          : "rgba(239,68,68,0.08)",
                      border: `1px solid ${
                        feedback.type === "success"
                          ? "rgba(16,185,129,0.4)"
                          : "rgba(239,68,68,0.4)"
                      }`,
                    }}
                  >
                    {feedback.type === "success" ? (
                      <CheckCircle
                        size={16}
                        color="#10b981"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                    ) : (
                      <AlertCircle
                        size={16}
                        color="#ef4444"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      />
                    )}
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color:
                          feedback.type === "success" ? "#10b981" : "#ef4444",
                        lineHeight: 1.4,
                      }}
                    >
                      {feedback.msg}
                    </span>
                  </div>
                )}

                {/* spacer */}
                <div style={{ flex: 1 }} />

                {/* action buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexDirection: isRTL ? "row-reverse" : "row",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={() => !saving && setOpen(false)}
                    disabled={saving}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 10,
                      border: `1px solid ${border}`,
                      background: "transparent",
                      color: textSecondary,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: saving ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {tr.cancel}
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={saving || !allowDefaultLocation}
                    style={{
                      padding: "10px 24px",
                      borderRadius: 10,
                      border: "none",
                      background: saving || !allowDefaultLocation
                        ? isDark
                          ? "rgba(46,124,186,0.5)"
                          : "rgba(46,124,186,0.4)"
                        : `linear-gradient(135deg, ${accent}, #2e7cba)`,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: saving || !allowDefaultLocation ? "not-allowed" : "pointer",
                      opacity: allowDefaultLocation ? 1 : 0.6,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      transition: "all 0.2s",
                      boxShadow: saving
                        ? "none"
                        : `0 4px 14px ${accent}55`,
                    }}
                    onMouseEnter={(e) => {
                      if (!saving)
                        e.currentTarget.style.background = `linear-gradient(135deg, ${accentHover}, #1e40af)`;
                    }}
                    onMouseLeave={(e) => {
                      if (!saving)
                        e.currentTarget.style.background = `linear-gradient(135deg, ${accent}, #2e7cba)`;
                    }}
                  >
                    {saving ? (
                      <Loader
                        size={15}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                    ) : (
                      <MapPin size={15} />
                    )}
                    {saving ? tr.saving : tr.save}
                  </button>
                </div>
              </div>
            </div>
          </div>
       </div>,
  document.body   // ← هنا سر الحل
)}

      {/* spin keyframe */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        /* Webkit scrollbar styling (Chrome, Edge, Safari) */
        div::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        div::-webkit-scrollbar-track {
          background: ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb {
          background: ${isDark ? "rgba(148,163,184,0.6)" : "rgba(100,116,139,0.5)"};
          border-radius: 10px;
          border: 2px solid ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)"};
        }
        
        div::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? "rgba(148,163,184,0.8)" : "rgba(100,116,139,0.7)"};
        }
      `}</style>
    </>
  );
};

export default DefaultLocationModal;